import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { CheckCircle2, Shield, TrendingUp, Users, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ScrollReveal } from "./ScrollReveal";
import Header from "./Header";
import Footer from "./Footer";
import GoogleReviews from "./googleReviews";

const RetirementPlanning = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  // Sticky CTA scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const triggerPoint = window.innerHeight * 0.25;
      setShowStickyCTA(scrolled > triggerPoint);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`/.netlify/functions/send-email`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        goal: "Retirement Planning",
        page_source: "retirement-planning",
        message: formData.goal.trim(),
        timestamp: new Date().toISOString(),
      });

      navigate(
        `/thank-you?service=retirement-planning&name=${encodeURIComponent(formData.name)}`,
      );
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Please try again or call us directly at 918.477.7227",
      });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-[#e8eef2] rounded-full text-[#3d4f5f] text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                Financial Advisors • Serving Oklahoma Residents
              </div>
              <h1 className="hero-headline text-5xl sm:text-6xl font-bold text-[#3d4f5f] mb-6 leading-tight">
                Retirement Planning in Oklahoma You Can Rely On
              </h1>
              <p className="hero-subheadline text-xl text-gray-600 mb-10">
                Personalized retirement strategies designed to help Oklahoma
                families retire with confidence and clarity.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() =>
                    document
                      .getElementById("consultation-form")
                      .scrollIntoView({ behavior: "smooth" })
                  }
                  className="btn-primary btn-cta-pulse bg-[#3d4f5f] hover:bg-[#2d3e4e] text-white px-8 py-6 text-lg rounded-lg shadow-lg"
                >
                  Schedule a No-Cost Retirement Consultation
                </Button>
                <Button
                  variant="outline"
                  className="sm:hidden border-2 border-[#3d4f5f] text-[#3d4f5f] px-8 py-6 text-lg rounded-lg hover:bg-[#f8fafb] transition-all duration-200"
                  onClick={() => (window.location.href = "tel:+19184777227")}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call a Local Advisor
                </Button>
              </div>
              <div className="hero-trust-badges flex flex-wrap justify-center lg:justify-start gap-8 mt-12 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Transparent Planning</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Client-First Approach</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Fiduciary Duty</span>
                </div>
              </div>

              {/* <GoogleReviews />*/}
            </div>

            {/* Right Image */}
            <div className="hidden lg:block hero-cta">
              <img
                src="https://images.pexels.com/photos/8439698/pexels-photo-8439698.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Mature couple planning retirement with financial advisor"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Retirement Planning Matters */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                Why Retirement Planning Matters
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Today's retirees face unique challenges that require careful,
                proactive planning
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <TrendingUp className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Rising Costs
                </h3>
                <p className="text-gray-600">
                  Healthcare and living expenses continue to increase, requiring
                  strategic planning to maintain your lifestyle in retirement.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <TrendingUp className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Market Volatility
                </h3>
                <p className="text-gray-600">
                  Navigate market uncertainty with a diversified strategy
                  designed to protect your retirement savings.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <TrendingUp className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Tax Complexity
                </h3>
                <p className="text-gray-600">
                  Optimize Social Security timing and withdrawal strategies to
                  minimize taxes and maximize your retirement income.
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
              Retirement Services We Offer
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive planning tailored to your retirement goals
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Retirement Income Planning",
              "401(k) & IRA Strategies",
              "Social Security Optimization",
              "Tax-Efficient Withdrawal Planning",
              "Risk Management & Asset Protection",
              "Medicare & Healthcare Planning",
            ].map((service, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <CheckCircle2 className="w-6 h-6 text-[#4a6274] flex-shrink-0 mt-1" />
                <span className="text-gray-800 font-medium">{service}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              onClick={() =>
                document
                  .getElementById("consultation-form")
                  .scrollIntoView({ behavior: "smooth" })
              }
              className="bg-[#4a6274] hover:bg-[#3a5263] text-white px-8 py-6 text-lg rounded-lg shadow-lg transition-all hover:shadow-xl hover:scale-105"
            >
              Start Your Retirement Plan
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
              Simple 3-Step Process
            </h2>
            <p className="text-lg text-gray-600">
              Getting started is easy and obligation-free
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Free Discovery Call",
                desc: "We learn about your retirement goals, current situation, and concerns in a no-pressure conversation.",
              },
              {
                step: "2",
                title: "Custom Retirement Roadmap",
                desc: "Receive a personalized retirement strategy designed specifically for your unique circumstances.",
              },
              {
                step: "3",
                title: "Ongoing Planning & Adjustments",
                desc: "Your plan evolves with you, ensuring you stay on track toward a confident retirement.",
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-[#3d4f5f] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 bg-white hover-lift">
              <div className="flex items-center gap-4 mb-4">
                <img
                  src="https://ui-avatars.com/api/?name=Chris+N&rounded=true&bold=true&format=svg"
                  alt="Client testimonial"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#4a6274]" />
                    <span className="font-semibold text-[#3d4f5f]">
                      Chris N
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Over the last 35 years, Curt has demonstrated the flexibility
                and dynamic knowledge of financial markets to turn what appears
                to be a questionable situation into success at age 54. I have
                doubts that I could have done this at all without his help. I
                recommend him without reservation."
              </p>
            </Card>
            <Card className="p-8 bg-white hover-lift">
              <div className="flex items-center gap-4 mb-4">
                <img
                  src="https://ui-avatars.com/api/?name=Pam+S&rounded=true&bold=true&format=svg"
                  alt="Client testimonial"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#4a6274]" />
                    <span className="font-semibold text-[#3d4f5f]">Pam S</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Curt Wilson has been my financial advisor/planner for about 6.5
                years. During that time I have seen the care he takes with my
                investments and appreciate greatly the time he spends with me
                explaining his thought processes and answering all my questions.
                His education, professionalism and personal sense of
                responsibility help me feel confident in all my dealings with
                him."
              </p>
            </Card>
          </div>
        </div>
      </section> */}

      {/* Consultation Form */}
      <section
        id="consultation-form"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
      >
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
              Plan Your Retirement With Confidence
            </h2>
            <p className="text-lg text-gray-600">
              Speak with an Oklahoma retirement planning advisor today
            </p>
          </div>
          <Card className="p-8 shadow-xl border-2 border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="918.477.7227"
                />
              </div>
              <div>
                <label
                  htmlFor="goal"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Primary Retirement Goal *
                </label>
                <Input
                  id="goal"
                  name="goal"
                  value={formData.goal}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="e.g., Plan my retirement income, Social Security optimization"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full bg-[#4a6274] hover:bg-[#3a5263] text-white py-6 text-lg font-semibold rounded-lg shadow-lg disabled:opacity-50"
              >
                {isSubmitting
                  ? "Submitting..."
                  : "Schedule a No-Cost Consultation"}
              </Button>
              <p className="text-sm text-gray-500 text-center">
                By submitting, you agree to be contacted about retirement
                planning services. No obligation.
              </p>
            </form>
          </Card>
        </div>
      </section>

      <Footer />

      {/* Sticky CTA Button (Mobile) */}
      <div
        className={`sticky-cta-mobile ${showStickyCTA ? "visible" : ""} fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 md:hidden z-40`}
      >
        <Button
          onClick={() =>
            document
              .getElementById("consultation-form")
              .scrollIntoView({ behavior: "smooth" })
          }
          className="btn-primary w-full bg-[#4a6274] hover:bg-[#3a5263] text-white py-4 text-base font-semibold rounded-lg shadow-lg"
        >
          Schedule a No-Cost Consultation
        </Button>
      </div>
    </div>
  );
};

export default RetirementPlanning;
